import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tag } from 'primereact/tag';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { Skeleton } from 'primereact/skeleton';
import { Dialog } from 'primereact/dialog';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import moment from 'moment';
import _ from 'lodash';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import VariableMappingDialog from './VariableMappingDialog';
import ScheduleConfiguration from './ScheduleConfiguration';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';

import './feeDecisionNotificationWorkflow.scss';

const FEE_DECISION_WORKFLOW_TYPES = {
    PRE_DUE_REMINDER: {
        type: 'pre-due-reminder',
        title: 'Pre-Due Reminder',
        subtitle: 'Notify parents before fee payment due date',
        apiPath: 'fee-pre-due-reminder',
        icon: 'pi pi-calendar',
        helpText: 'Sends payment reminders X days before fee installment due date. Configure the number of days in advance for the reminder.',
        channelMetadata: {
            email: {
                caption: 'Send pre-due reminders with payment details and due date'
            }
        }
    },
    POST_DUE_REMINDER: {
        type: 'post-due-reminder',
        title: 'Post-Due Reminder',
        subtitle: 'Notify parents about overdue fee payments',
        apiPath: 'fee-post-due-reminder',
        icon: 'pi pi-exclamation-circle',
        helpText: 'Sends reminders Y days after due date if payment not received. Configure the number of days after due date for the reminder.',
        channelMetadata: {
            email: {
                caption: 'Send overdue payment notifications with outstanding amount'
            }
        }
    },
    HIGH_RISK_ESCALATION: {
        type: 'high-risk-escalation',
        title: 'High-Risk Escalation',
        subtitle: 'Escalate critical risk students to management',
        apiPath: 'fee-high-risk-escalation',
        icon: 'pi pi-exclamation-triangle',
        helpText: 'Triggers when a student\'s risk band becomes Critical or overdue exceeds threshold. Sends notification to Principal/Management for intervention.',
        channelMetadata: {
            email: {
                caption: 'Escalate high-risk cases with detailed risk analysis'
            }
        }
    },
    PROMISE_BREACHED: {
        type: 'promise-breached',
        title: 'Promise Breached',
        subtitle: 'Notify when promised payment date is missed',
        apiPath: 'fee-promise-breached',
        icon: 'pi pi-calendar-times',
        helpText: 'Triggers when a parent misses a promised payment date. Automatically assigns follow-up task or sends reminder.',
        channelMetadata: {
            email: {
                caption: 'Send promise breach notifications with payment history'
            }
        }
    }
};

const CHANNEL_METADATA = {
    sms: {
        title: 'SMS Notifications',
        caption: 'Send instant text messages to guardians',
        icon: 'pi pi-mobile',
        templateKey: 'sms',
        templateLabel: 'SMS Template'
    },
    email: {
        title: 'Email Notifications',
        caption: 'Deliver detailed summaries by email',
        icon: 'pi pi-envelope',
        templateKey: 'email',
        templateLabel: 'Email Template'
    },
    whatsapp: {
        title: 'WhatsApp Notifications',
        caption: 'Notify parents on WhatsApp using approved templates',
        icon: 'pi pi-whatsapp',
        templateKey: 'whatsapp',
        templateLabel: 'WhatsApp Template'
    },
    alert: {
        title: 'In-app Alerts',
        caption: 'Push alerts to mobile apps for parents or students',
        icon: 'pi pi-bell',
        templateKey: 'push',
        templateLabel: 'Push Template'
    }
};

const CONFIG_STATUS_META = {
    CONFIGURED: { severity: 'success', label: 'Configured' },
    MISSING_CONFIGURATION: { severity: 'danger', label: 'Missing config' },
    READ_ONLY: { severity: 'info', label: 'Managed externally' },
    DISABLED: { severity: 'warning', label: 'Disabled' }
};

const WORKFLOW_STATUS_OPTIONS = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Ready', value: 'READY' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Disabled', value: 'DISABLED' }
];

const DELIVERY_OPTIONS = [
    { label: 'Manual Only', value: 'MANUAL_ONLY' },
    { label: 'Daily', value: 'DAILY' },
    { label: 'Weekly', value: 'WEEKLY' },
    { label: 'Schedule Once', value: 'SCHEDULE_ONCE' }
];

const getConfigMeta = (configurationStatus) => {
    return CONFIG_STATUS_META[configurationStatus] || { severity: 'secondary', label: configurationStatus || 'Unknown' };
};

const FeeDecisionNotificationWorkflow = () => {
    const toastRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [workflows, setWorkflows] = useState({});
    const [templates, setTemplates] = useState({});
    const [walletBalance, setWalletBalance] = useState(0);
    const [configurationSummary, setConfigurationSummary] = useState({});
    const [hasChanges, setHasChanges] = useState({});
    const [error, setError] = useState(null);
    const originalWorkflowsRef = useRef({});
    const serviceRef = useRef(new Service());

    const [templateDialogVisible, setTemplateDialogVisible] = useState(false);
    const [templateDialogActiveChannel, setTemplateDialogActiveChannel] = useState('sms');
    const [templateDialogSelected, setTemplateDialogSelected] = useState(null);
    const [templateDialogWorkflowApiPath, setTemplateDialogWorkflowApiPath] = useState(null);
    const [templateMappingStatus, setTemplateMappingStatus] = useState({});

    const [previewDialogVisible, setPreviewDialogVisible] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);

    const [variableMappingDialogVisible, setVariableMappingDialogVisible] = useState(false);
    const [variableMappingData, setVariableMappingData] = useState({
        template: null,
        variables: [],
        availableFields: [],
        existingMappings: {},
        channelKey: null,
        chosenTemplate: null,
    });
    const [loadingVariables, setLoadingVariables] = useState(false);

    const [activeTab, setActiveTab] = useState(0);
    const [addChannelDialogVisible, setAddChannelDialogVisible] = useState(false);
    const [addChannelWorkflowApiPath, setAddChannelWorkflowApiPath] = useState(null);

    const fetchWorkflow = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const workflowTypes = Object.values(FEE_DECISION_WORKFLOW_TYPES);
            const fetchPromises = workflowTypes.map(config =>
                serviceRef.current.get(`${baseUrlAdmin}/fee-decision-workflows/${config.apiPath}`, true)
                    .then(response => ({ apiPath: config.apiPath, config, response }))
                    .catch(error => ({ apiPath: config.apiPath, config, error }))
            );

            const results = await Promise.all(fetchPromises);

            const workflowsData = {};
            const originalData = {};
            let sharedTemplates = {};
            let sharedWalletBalance = 0;
            let sharedConfigSummary = {};

            results.forEach(({ apiPath, config, response, error }) => {
                if (error) {
                    console.warn(`[FeeDecisionWorkflow] Failed to fetch ${config.title}:`, error);
                    const defaultWorkflow = {
                        name: config.title,
                        description: config.subtitle,
                        status: 'DRAFT',
                        isEnabled: false,
                        settings: {
                            delivery: 'MANUAL_ONLY',
                            schedule: {},
                            triggerOffset: config.type === 'pre-due-reminder' ? -3 : (config.type === 'post-due-reminder' ? 7 : 0)
                        },
                        channels: [
                            { channelKey: 'sms', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'email', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'whatsapp', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'alert', isEnabled: false, configurationStatus: 'MISSING_CONFIGURATION' }
                        ],
                        configurationSummary: {},
                        updatedAt: new Date().toISOString()
                    };
                    workflowsData[apiPath] = defaultWorkflow;
                    originalData[apiPath] = _.cloneDeep(defaultWorkflow);
                } else if (response?.status && response?.res?.status) {
                    const data = response.res.data || {};
                    const fetchedWorkflow = data.workflow || {};

                    console.log(`[FeeDecisionWorkflow] Fetched ${config.title}:`, fetchedWorkflow);

                    workflowsData[apiPath] = fetchedWorkflow;
                    originalData[apiPath] = _.cloneDeep(fetchedWorkflow);

                    // Set shared data from first successful response
                    if (Object.keys(sharedTemplates).length === 0) {
                        sharedTemplates = data.templates || {};
                        sharedWalletBalance = data.walletBalance || 0;
                        sharedConfigSummary = fetchedWorkflow.configurationSummary || {};

                        console.log('[FeeDecisionWorkflow] Setting shared data:', {
                            templatesCount: Object.keys(sharedTemplates).length,
                            walletBalance: sharedWalletBalance,
                            configSummary: sharedConfigSummary
                        });

                        // Initialize mapping status for templates already assigned across all workflows
                        const initialMappingStatus = {};
                        results.forEach(({ response: r }) => {
                            if (r?.status && r?.res?.status) {
                                const wf = r.res.data?.workflow;
                                (wf?.channels || []).forEach((channel) => {
                                    const templateId = channel?.templateId ? String(channel.templateId) : null;
                                    if (templateId && channel?.variableMappings && Object.keys(channel.variableMappings).length > 0) {
                                        initialMappingStatus[templateId] = {
                                            mapped: true,
                                            mappings: channel.variableMappings
                                        };
                                    }
                                });
                            }
                        });
                        setTemplateMappingStatus(initialMappingStatus);
                    }
                } else {
                    console.warn(`[FeeDecisionWorkflow] Invalid response for ${config.title}:`, response);
                    const defaultWorkflow = {
                        name: config.title,
                        description: config.subtitle,
                        status: 'DRAFT',
                        isEnabled: false,
                        settings: {
                            delivery: 'MANUAL_ONLY',
                            schedule: {},
                            triggerOffset: config.type === 'pre-due-reminder' ? -3 : (config.type === 'post-due-reminder' ? 7 : 0)
                        },
                        channels: [
                            { channelKey: 'sms', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'email', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'whatsapp', isEnabled: false, configurationStatus: 'CONFIGURED' },
                            { channelKey: 'alert', isEnabled: false, configurationStatus: 'MISSING_CONFIGURATION' }
                        ],
                        configurationSummary: {},
                        updatedAt: new Date().toISOString()
                    };
                    workflowsData[apiPath] = defaultWorkflow;
                    originalData[apiPath] = _.cloneDeep(defaultWorkflow);
                }
            });

            console.log('[FeeDecisionWorkflow] Final workflows data:', workflowsData);

            originalWorkflowsRef.current = originalData;
            setWorkflows(workflowsData);
            setTemplates(sharedTemplates);
            setWalletBalance(sharedWalletBalance);
            setConfigurationSummary(sharedConfigSummary);
            setHasChanges({});
        } catch (err) {
            console.error('[FeeDecisionWorkflow] fetch error', err);
            setError(err?.message || 'Failed to load workflows');
            toastRef.current?.show({
                severity: 'error',
                summary: 'Failed to load workflows',
                detail: err?.message || 'An unexpected error occurred',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchWorkflow();
    }, [fetchWorkflow]);

    const markDirty = (apiPath) => {
        setHasChanges(prev => ({ ...prev, [apiPath]: true }));
    };

    const updateChannel = (apiPath, channelKey, updates) => {
        setWorkflows(prev => {
            const workflow = prev[apiPath];
            if (!workflow) return prev;
            
            const channels = workflow.channels.map(ch =>
                ch.channelKey === channelKey ? { ...ch, ...updates } : ch
            );
            
            return {
                ...prev,
                [apiPath]: {
                    ...workflow,
                    channels
                }
            };
        });
        markDirty(apiPath);
    };

    const updateWorkflowSettings = (apiPath, updates) => {
        setWorkflows(prev => ({
            ...prev,
            [apiPath]: {
                ...prev[apiPath],
                settings: {
                    ...prev[apiPath]?.settings,
                    ...updates
                }
            }
        }));
        markDirty(apiPath);
    };

    const handleSave = async (apiPath) => {
        const workflow = workflows[apiPath];
        if (!workflow) return;

        setSaving(prev => ({ ...prev, [apiPath]: true }));
        try {
            // Get academicYear from localStorage
            const academicYear = localStorage.getItem('userAcademicYear') || '';
            
            // Clean workflow payload - only send fields that should be saved
            const cleanedWorkflow = {
                name: workflow.name,
                description: workflow.description,
                status: workflow.status,
                isEnabled: workflow.isEnabled,
                academicYear: academicYear,
                channels: workflow.channels.map(channel => {
                    // Only include fields that belong to the channel schema
                    const cleanedChannel = {
                        channelKey: channel.channelKey,
                        isEnabled: channel.isEnabled,
                        templateId: channel.templateId || null,
                        templateName: channel.templateName || '',
                        templateType: channel.templateType,
                        templateContent: channel.templateContent || null,
                        variableMappings: channel.variableMappings || {},
                        isReadOnly: channel.isReadOnly || false
                    };
                    return cleanedChannel;
                }),
                settings: {
                    delivery: workflow.settings?.delivery || 'MANUAL_ONLY',
                    ...(workflow.settings?.triggerOffset !== undefined && { triggerOffset: workflow.settings.triggerOffset }),
                    ...(workflow.settings?.overdueDays !== undefined && { overdueDays: workflow.settings.overdueDays })
                }
            };

            const response = await serviceRef.current.put(
                `${baseUrlAdmin}/fee-decision-workflows/${apiPath}`,
                { workflow: cleanedWorkflow },
                true
            );

            if (response?.status && response?.res?.status) {
                const updatedWorkflow = response.res.data?.workflow || workflow;
                originalWorkflowsRef.current[apiPath] = _.cloneDeep(updatedWorkflow);
                setWorkflows(prev => ({ ...prev, [apiPath]: updatedWorkflow }));
                setHasChanges(prev => ({ ...prev, [apiPath]: false }));
                toastRef.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Workflow saved successfully',
                    life: 3000
                });
            } else {
                throw new Error(response?.errMessage || 'Failed to save workflow');
            }
        } catch (error) {
            console.error('[handleSave] Error:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to save workflow',
                life: 5000
            });
        } finally {
            setSaving(prev => ({ ...prev, [apiPath]: false }));
        }
    };

    const handleCancel = (apiPath) => {
        confirmDialog({
            message: 'Are you sure you want to discard your changes?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                setWorkflows(prev => ({
                    ...prev,
                    [apiPath]: _.cloneDeep(originalWorkflowsRef.current[apiPath])
                }));
                setHasChanges(prev => ({ ...prev, [apiPath]: false }));
                toastRef.current?.show({
                    severity: 'info',
                    summary: 'Cancelled',
                    detail: 'Changes discarded',
                    life: 3000
                });
            }
        });
    };

    const handleRunNow = (apiPath) => {
        const workflowConfig = Object.values(FEE_DECISION_WORKFLOW_TYPES).find(w => w.apiPath === apiPath);
        let selectedDate = new Date();

        confirmDialog({
            message: (
                <div>
                    <p>Select the date for which you want to process notifications:</p>
                    <div className="p-field" style={{ marginTop: '1rem' }}>
                        <label htmlFor="runNowDate" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Process for date:
                        </label>
                        <Calendar
                            id="runNowDate"
                            value={selectedDate}
                            onChange={(e) => { selectedDate = e.value; }}
                            dateFormat="dd/mm/yy"
                            maxDate={new Date()}
                            showIcon
                            style={{ width: '100%' }}
                        />
                        <small className="p-text-secondary" style={{ display: 'block', marginTop: '0.5rem' }}>
                            You can select today or any past date to process
                        </small>
                    </div>
                </div>
            ),
            header: 'Run Workflow Now',
            icon: 'pi pi-play',
            accept: async () => {
                if (!selectedDate) {
                    toastRef.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Please select a date',
                        life: 3000
                    });
                    return;
                }

                try {
                    const academicYear = localStorage.getItem('userAcademicYear');
                    const formattedDate = moment(selectedDate).format('YYYY-MM-DD');
                    
                    const response = await serviceRef.current.post(
                        `${baseUrlAdmin}/fee-decision-workflows/${apiPath}/run-now`,
                        {
                            date: formattedDate,
                            academicYear: academicYear
                        },
                        true
                    );

                    if (response?.status && response?.res?.status) {
                        toastRef.current?.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: `${workflowConfig?.title} executed successfully`,
                            life: 3000
                        });
                        fetchWorkflow();
                    } else {
                        throw new Error(response?.errMessage || response?.res?.message || 'Failed to execute workflow');
                    }
                } catch (error) {
                    console.error('Error executing workflow:', error);
                    toastRef.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to execute workflow',
                        life: 3000
                    });
                }
            },
            acceptLabel: 'Run Now',
            rejectLabel: 'Cancel'
        });
    };

    const openTemplateChooser = (apiPath, channelKey) => {
        const workflow = workflows[apiPath];
        
        // Initialize mapping status for ALL channels in this workflow, not just the current one
        const initialMappingStatus = {};
        (workflow?.channels || []).forEach((channel) => {
            const templateId = channel?.templateId ? String(channel.templateId) : null;
            if (templateId && channel?.variableMappings && Object.keys(channel.variableMappings).length > 0) {
                initialMappingStatus[templateId] = {
                    mapped: true,
                    mappings: channel.variableMappings
                };
            }
        });
        setTemplateMappingStatus(initialMappingStatus);
        
        // Set the selected template for the current channel being viewed
        const channel = workflow?.channels?.find(ch => ch.channelKey === channelKey);
        const templateList = templates[channelKey] || templates[CHANNEL_METADATA[channelKey]?.templateKey] || [];
        const templateId = channel?.templateId ? String(channel.templateId) : null;
        const selected = templateList.find(t => String(t._id) === templateId) || null;

        setTemplateDialogWorkflowApiPath(apiPath);
        setTemplateDialogActiveChannel(channelKey);
        setTemplateDialogSelected(selected);
        setTemplateDialogVisible(true);
    };

    const handleTemplateAssign = async (apiPath, channelKey, chosenTemplate) => {
        if (!chosenTemplate) return;

        const mappingInfo = templateMappingStatus[chosenTemplate._id];
        if (!mappingInfo?.mapped) {
            toastRef.current?.show({
                severity: 'warn',
                summary: 'Mapping Required',
                detail: 'Please map template variables before selecting this template',
                life: 3000
            });
            return;
        }

        // Fetch template content
        const variableData = await fetchTemplateVariables(
            chosenTemplate._id,
            chosenTemplate.channelKey || CHANNEL_METADATA[channelKey]?.templateKey || channelKey,
            channelKey
        );

        if (!variableData) {
            return;
        }

        // Get the current workflow and channel to check configuration status
        const workflow = workflows[apiPath];
        const channel = workflow?.channels?.find(ch => ch.channelKey === channelKey);
        const canAutoEnable = workflow?.isEnabled && channel?.configurationStatus === 'CONFIGURED';

        updateChannel(apiPath, channelKey, {
            templateId: chosenTemplate._id,
            templateName: chosenTemplate.name,
            templateContent: variableData.template.content,
            variableMappings: mappingInfo.mappings,
            // Auto-enable the channel if workflow is enabled and channel is configured
            isEnabled: canAutoEnable ? true : (channel?.isEnabled || false)
        });

        markDirty(apiPath);
        setTemplateDialogVisible(false);
        toastRef.current?.show({
            severity: 'success',
            summary: 'Template Assigned',
            detail: `Template "${chosenTemplate.name}" assigned successfully`,
            life: 3000
        });
    };

    const fetchTemplateVariables = async (templateId, templateType, channelKey) => {
        setLoadingVariables(true);
        try {
            const response = await serviceRef.current.post(
                `${baseUrlAdmin}/notification-workflows/template/variables`,
                {
                    templateId,
                    templateType,
                    workflowType: templateDialogWorkflowApiPath
                },
                true
            );

            if (response?.status && response?.res?.status) {
                return response.res.data;
            } else {
                throw new Error(response?.errMessage || response?.res?.message || 'Failed to fetch template variables');
            }
        } catch (error) {
            console.error('[fetchTemplateVariables] Error:', error);
            toastRef.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to fetch template variables',
                life: 3000,
            });
            return null;
        } finally {
            setLoadingVariables(false);
        }
    };

    const handleOpenMappingForTemplate = async (template, channelKey) => {
        const variableData = await fetchTemplateVariables(
            template._id,
            template.channelKey || CHANNEL_METADATA[channelKey]?.templateKey || channelKey,
            channelKey
        );

        if (!variableData) return;

        if (!variableData.hasVariables || variableData.variables.length === 0) {
            setTemplateMappingStatus(prev => ({
                ...prev,
                [template._id]: {
                    mapped: true,
                    mappings: {}
                }
            }));
            toastRef.current?.show({
                severity: 'info',
                summary: 'No Variables',
                detail: 'This template does not contain any variables to map',
                life: 3000,
            });
            return;
        }

        const availableFields = Array.isArray(variableData.availableFields)
            ? variableData.availableFields
            : [];

        if (availableFields.length === 0) {
            toastRef.current?.show({
                severity: 'error',
                summary: 'Configuration Error',
                detail: 'No field mappings available. Please contact support.',
                life: 5000,
            });
            return;
        }

        const existingMappings = templateMappingStatus[template._id]?.mappings || {};

        setVariableMappingData({
            template: variableData.template,
            variables: variableData.variables || [],
            availableFields: availableFields,
            existingMappings,
            channelKey,
            chosenTemplate: template,
        });
        setVariableMappingDialogVisible(true);
    };

    const handleSaveVariableMappings = async (mappings) => {
        const { chosenTemplate, channelKey } = variableMappingData;
        const apiPath = templateDialogWorkflowApiPath;

        setTemplateMappingStatus(prev => ({
            ...prev,
            [chosenTemplate._id]: {
                mapped: true,
                mappings: mappings
            }
        }));

        const workflow = workflows[apiPath];
        const channel = workflow?.channels?.find(ch => ch.channelKey === channelKey);
        if (channel && channel.templateId === chosenTemplate._id) {
            updateChannel(apiPath, channelKey, {
                variableMappings: mappings
            });
        }

        toastRef.current?.show({
            severity: 'success',
            summary: 'Variables Mapped',
            detail: `Template "${chosenTemplate.name}" variables mapped successfully.`,
            life: 3000,
        });

        setVariableMappingDialogVisible(false);
    };

    const canToggleChannel = (workflow, channel) => {
        if (!channel) return false;
        if (!workflow?.isEnabled) return false;
        if (channel.isReadOnly) return false;
        if (!channel.templateId) return false;
        return channel.configurationStatus === 'CONFIGURED';
    };

    const renderChannelChip = (workflow, apiPath, channel) => {
        const meta = CHANNEL_METADATA[channel.channelKey] || {};
        const toggleDisabled = !canToggleChannel(workflow, channel);
        const isChecked = !!channel.isEnabled;
        const hasTemplate = !!channel.templateId;
        const hasMappings = channel.variableMappings && Object.keys(channel.variableMappings).length > 0;

        return (
            <div key={channel.channelKey} className={`mode-chip ${isChecked ? 'checked' : ''} ${hasTemplate ? 'has-template' : ''}`}>
                <label htmlFor={`mode-${apiPath}-${channel.channelKey}`}>
                    {meta.title?.split(' ')[0] || channel.channelKey}
                    {hasTemplate && (
                        <span className="template-indicator" title={channel.templateName}>
                            ({channel.templateName})
                            {hasMappings && (
                                <i className="pi pi-link" style={{ marginLeft: '0.25rem', fontSize: '0.75rem', color: '#4caf50' }} title="Variables mapped" />
                            )}
                        </span>
                    )}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {hasTemplate && (
                        <>
                            <Button
                                icon="pi pi-pencil"
                                className="p-button-text p-button-sm"
                                onClick={() => openTemplateChooser(apiPath, channel.channelKey)}
                                tooltip="Edit template mappings"
                                tooltipOptions={{ position: 'top' }}
                                style={{ padding: '0.25rem' }}
                            />
                            <Button
                                icon="pi pi-times"
                                className="p-button-text p-button-sm p-button-danger"
                                onClick={() => handleRemoveTemplate(apiPath, channel.channelKey)}
                                tooltip="Remove template"
                                tooltipOptions={{ position: 'top' }}
                                style={{ padding: '0.25rem' }}
                            />
                        </>
                    )}
                    <Checkbox
                        inputId={`mode-${apiPath}-${channel.channelKey}`}
                        onChange={(e) => updateChannel(apiPath, channel.channelKey, { isEnabled: e.checked })}
                        checked={isChecked}
                        disabled={toggleDisabled}
                        {...(
                            !workflow?.isEnabled
                                ? { tooltip: 'Enable the workflow first', tooltipOptions: { position: 'top' } }
                                : !channel.templateId
                                    ? { tooltip: 'Assign a template first by clicking the + button', tooltipOptions: { position: 'top' } }
                                    : channel.configurationStatus !== 'CONFIGURED'
                                        ? { tooltip: `${meta.title} is not configured`, tooltipOptions: { position: 'top' } }
                                        : {}
                        )}
                    />
                </div>
            </div>
        );
    };

    const handleRemoveTemplate = (apiPath, channelKey) => {
        confirmDialog({
            message: 'Are you sure you want to remove this template?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                updateChannel(apiPath, channelKey, {
                    templateId: null,
                    templateName: null,
                    variableMappings: {},
                    isEnabled: false
                });
                toastRef.current?.show({
                    severity: 'info',
                    summary: 'Template Removed',
                    detail: 'Template has been removed',
                    life: 3000
                });
            }
        });
    };

    const renderWorkflowCard = (config) => {
        const apiPath = config.apiPath;
        const workflow = workflows[apiPath];
        
        console.log(`[renderWorkflowCard] Rendering ${config.title}:`, {
            apiPath,
            workflow,
            isEnabled: workflow?.isEnabled,
            channels: workflow?.channels,
            settings: workflow?.settings
        });
        
        if (!workflow) return null;

        const enabledChannels = workflow.channels?.filter(ch => ch.isEnabled) || [];
        const triggerOffset = workflow.settings?.triggerOffset || 0;
        const overdueDaysThreshold = workflow.settings?.overdueDays || 30;

        return (
            <div key={apiPath} className="workflow-notification-card">
                <div className="notification-header">
                    <h3 className="notification-title">
                        {config.title}
                        <Tooltip target=".info-icon" />
                        <i className="pi pi-info-circle info-icon" data-pr-tooltip={config.helpText} />
                    </h3>
                    <div className="notification-header-right">
                        <Button
                            label="Cancel"
                            className="cancel-btn"
                            onClick={() => handleCancel(apiPath)}
                            disabled={!hasChanges[apiPath] || saving[apiPath]}
                            outlined
                            style={{
                                marginRight: '0.5rem',
                                color: hasChanges[apiPath] ? '#495057' : '#6c757d',
                                borderColor: hasChanges[apiPath] ? '#ced4da' : '#dee2e6',
                                backgroundColor: 'transparent'
                            }}
                        />
                        <Authorizer permId={PERMISSIONS.FDE_SAVE_WORKFLOW}>
                            <Button
                                label={saving[apiPath] ? "Saving..." : "Save"}
                                icon={saving[apiPath] ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                className="save-btn"
                                onClick={() => handleSave(apiPath)}
                                disabled={!hasChanges[apiPath] || saving[apiPath]}
                                style={{
                                    marginRight: '0.5rem',
                                    backgroundColor: hasChanges[apiPath] ? '#3b82f6' : '#94a3b8',
                                    borderColor: hasChanges[apiPath] ? '#3b82f6' : '#94a3b8',
                                    color: '#ffffff'
                                }}
                            />
                        </Authorizer>
                        <Authorizer permId={PERMISSIONS.FDE_TEST_WORKFLOW}>
                            <Button
                                label="Run Now"
                                className="run-now-btn"
                                onClick={() => handleRunNow(apiPath)}
                                disabled={!workflow.isEnabled}
                                tooltip={!workflow.isEnabled ? 'Enable the workflow to run it manually' : 'Manually trigger this workflow'}
                                tooltipOptions={{ position: 'top' }}
                            />
                        </Authorizer>
                    </div>
                    <Authorizer permId={workflow.isEnabled ? PERMISSIONS.FDE_DISABLE_WORKFLOW : PERMISSIONS.FDE_ENABLE_WORKFLOW}>
                        <InputSwitch
                            checked={workflow.isEnabled}
                            onChange={(e) => {
                                setWorkflows(prev => ({
                                    ...prev,
                                    [apiPath]: {
                                        ...prev[apiPath],
                                        isEnabled: e.value
                                    }
                                }));
                                markDirty(apiPath);
                            }}
                            className="workflow-toggle"
                        />
                    </Authorizer>
                </div>

                <div className="notification-body">
                    {(config.type === 'pre-due-reminder' || config.type === 'post-due-reminder') && (
                        <div className="trigger-offset-section">
                            <label className="trigger-label">
                                {config.type === 'pre-due-reminder' 
                                    ? 'Send reminder (days before due date):' 
                                    : 'Send reminder (days after due date):'}
                            </label>
                            <InputNumber
                                value={Math.abs(triggerOffset)}
                                onValueChange={(e) => {
                                    const value = config.type === 'pre-due-reminder' ? -(e.value || 0) : (e.value || 0);
                                    updateWorkflowSettings(apiPath, { triggerOffset: value });
                                }}
                                min={0}
                                max={30}
                                showButtons
                                buttonLayout="horizontal"
                                step={1}
                                className="trigger-input"
                            />
                        </div>
                    )}

                    {config.type === 'high-risk-escalation' && (
                        <div className="trigger-offset-section">
                            <label className="trigger-label">
                                Overdue Threshold (Days):
                            </label>
                            <InputNumber
                                value={overdueDaysThreshold}
                                onValueChange={(e) => {
                                    updateWorkflowSettings(apiPath, { overdueDays: e.value || 0 });
                                }}
                                min={1}
                                max={365}
                                showButtons
                                buttonLayout="horizontal"
                                step={1}
                                className="trigger-input"
                                tooltip="Trigger escalation if payment is overdue by more than these days"
                            />
                        </div>
                    )}

                    <div className="modes-row" style={{ opacity: workflow.isEnabled ? 1 : 0.6 }}>
                        {(workflow.channels || []).map((channel) => renderChannelChip(workflow, apiPath, channel))}
                        <Button
                            className="mode-plus-button"
                            icon="pi pi-plus"
                            rounded
                            text
                            severity="primary"
                            onClick={() => openTemplateChooser(apiPath, 'sms')}
                            aria-label="Configure Templates"
                            disabled={!workflow.isEnabled}
                            tooltip={!workflow.isEnabled ? 'Enable the workflow to configure templates' : 'Configure Templates'}
                        />
                    </div>

                    <div className="channels-configured-text">
                        <i className="pi pi-check-circle" />
                        {enabledChannels.length} of {workflow.channels?.length || 0} channels configured
                    </div>

                    <div className="schedule-row">
                        <label className="schedule-label">Schedule</label>
                        <Dropdown
                            value={workflow.settings?.delivery || 'MANUAL_ONLY'}
                            options={DELIVERY_OPTIONS}
                            onChange={(e) => updateWorkflowSettings(apiPath, { delivery: e.value })}
                            placeholder="Select schedule"
                            className="schedule-dropdown"
                        />
                    </div>

                    <div className="notification-footer-info">
                        <span className="footer-text">Last Triggered: {workflow.lastTriggered || 'Never'}</span>
                        <span className="footer-text">Next Scheduled: {workflow.settings?.delivery === 'MANUAL_ONLY' ? 'Manual Only' : 'Configured'}</span>
                        <span className="footer-text">Last updated: {moment(workflow.updatedAt).fromNow()}</span>
                    </div>

                    {/* Workflow disabled indicator */}
                    {!workflow.isEnabled && (
                        <div className="workflow-disabled-banner" style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            marginTop: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#6b7280'
                        }}>
                            <i className="pi pi-info-circle" style={{ fontSize: '1rem' }}></i>
                            <span style={{ fontWeight: '500' }}>This workflow is currently disabled. Enable the toggle above to activate automatic notifications.</span>
                        </div>
                    )}

                    {/* Unsaved changes indicator */}
                    {hasChanges[apiPath] && (
                        <div className="unsaved-changes-banner" style={{
                            padding: '0.75rem 1rem',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffc107',
                            borderRadius: '6px',
                            marginTop: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: '#856404'
                        }}>
                            <i className="pi pi-exclamation-triangle" style={{ fontSize: '1rem' }}></i>
                            <span style={{ fontWeight: '500' }}>You have unsaved changes. Click "Save" to apply your changes or "Cancel" to discard them.</span>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="fee-decision-notification-workflow-page">
            <Toast ref={toastRef} position="bottom-right" />
            <ConfirmDialog />

            {loading && (
                <div className="workflow-loading">
                    <Skeleton height="200px" className="mb-3" />
                    <Skeleton height="200px" className="mb-3" />
                    <Skeleton height="200px" className="mb-3" />
                    <Skeleton height="200px" />
                </div>
            )}

            {!loading && Object.keys(workflows).length > 0 && (
                <>
                    {/* <div className="workflow-header-card">
                        <div className="workflow-header-content">
                            <h2>Fee Decision Notification Workflows</h2>
                            <p>Configure automated fee reminders and escalation notifications</p>
                        </div>
                        <div className="wallet-pill">
                            Wallet Balance: ₹{walletBalance.toFixed(2)}
                        </div>
                    </div> */}

                    <div className="configure-section">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h2 className="section-title" style={{ margin: 0 }}>Configure</h2>
                            <div className="wallet-pill">Wallet Balance: ₹{walletBalance.toFixed(2)}</div>
                        </div>
                        <div className="configure-cards">
                            {Object.keys(CHANNEL_METADATA).map((channelKey) => {
                                const meta = CHANNEL_METADATA[channelKey];
                                const rawStatus = configurationSummary[channelKey] || 'MISSING_CONFIGURATION';
                                const status = rawStatus.toLowerCase();
                                
                                const getStatusText = (status, channelKey) => {
                                    switch (status) {
                                        case 'configured':
                                            return `Our ${meta.title} is integrated and active`;
                                        case 'missing_configuration':
                                            return `Our ${meta.title.split(' ')[0]} gateway not configured`;
                                        case 'read_only':
                                            return `${meta.title} managed externally`;
                                        case 'disabled':
                                            return `${meta.title} service is disabled`;
                                        default:
                                            return `${meta.title} status unknown`;
                                    }
                                };

                                return (
                                    <div key={channelKey} className={`config-card ${status}`}>
                                        <div className="config-icon">
                                            <i className={meta.icon}></i>
                                        </div>
                                        <div className="config-content">
                                            <h4>{channelKey === 'alert' ? 'Push Notification' : meta.title.split(' ')[0]}</h4>
                                            <p className="config-status">{getStatusText(status, channelKey)}</p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="notification-types-section">
                        <h3 className="section-title">Notification Types</h3>
                        <div className="workflow-cards-container">
                            {Object.values(FEE_DECISION_WORKFLOW_TYPES).map(config => renderWorkflowCard(config))}
                        </div>
                    </div>

                    <Dialog
                        header="Select Template"
                        visible={templateDialogVisible}
                        style={{ width: '60vw', maxWidth: 900 }}
                        onHide={() => setTemplateDialogVisible(false)}
                    >
                        <TabView 
                            activeIndex={['sms', 'whatsapp', 'email', 'alert'].indexOf(templateDialogActiveChannel)}
                            onTabChange={(e) => {
                                const newChannelKey = ['sms', 'whatsapp', 'email', 'alert'][e.index];
                                setTemplateDialogActiveChannel(newChannelKey);

                                const workflow = workflows[templateDialogWorkflowApiPath];
                                const channel = (workflow?.channels || []).find((c) => c.channelKey === newChannelKey);
                                const templateList = templates[newChannelKey] || templates[CHANNEL_METADATA[newChannelKey]?.templateKey] || [];
                                const templateId = channel?.templateId ? String(channel.templateId) : null;
                                const selected = templateList.find((t) => String(t._id) === templateId) || null;
                                setTemplateDialogSelected(selected);
                            }}
                        >
                            {['sms', 'whatsapp', 'email', 'alert'].map((key) => {
                                const showTemplateId = key === 'sms' || key === 'whatsapp';
                                
                                const getContent = (row) => {
                                    let content = '';
                                    if (key === 'sms') content = row?.content || '';
                                    else if (key === 'whatsapp') content = row?.body || '';
                                    else if (key === 'email') {
                                        const subject = row?.subject || '';
                                        const body = row?.body || '';
                                        content = subject && body ? `Subject: ${subject}\n\n${body}` : (subject || body);
                                    } else if (key === 'alert') content = row?.description || '';
                                    
                                    if (!content) return `No content available (${key})`;
                                    return content.length > 100 ? content.slice(0, 100) + '...' : content;
                                };

                                const getTemplateId = (row, channelKey) => {
                                    if (channelKey === 'whatsapp') {
                                        return row?.templateId || 'No template ID';
                                    }
                                    return row?.template_id || row?.templateId || 'No template ID';
                                };

                                const showFullContent = (row) => {
                                    setPreviewTemplate({
                                        ...row,
                                        channelKey: key,
                                        showTemplateId
                                    });
                                    setPreviewDialogVisible(true);
                                };

                                const hasVariables = (row) => {
                                    let content = '';
                                    if (key === 'sms') content = row?.content || '';
                                    else if (key === 'whatsapp') content = row?.body || '';
                                    else if (key === 'email') content = `${row?.subject || ''} ${row?.body || ''}`;
                                    else if (key === 'alert') content = row?.description || '';
                                    return /\{\{\d+\}\}/.test(content);
                                };

                                const isMapped = (row) => templateMappingStatus[row._id]?.mapped || false;

                                const canSelect = (row) => {
                                    return !hasVariables(row) || isMapped(row);
                                };

                                return (
                                    <TabPanel header={CHANNEL_METADATA[key]?.title?.split(' ')[0] || key} key={key}>
                                        <DataTable 
                                            value={(templates[key] || templates[CHANNEL_METADATA[key]?.templateKey] || [])} 
                                            paginator 
                                            rows={5} 
                                            responsiveLayout="scroll"
                                        >
                                            <Column 
                                                header="Select" 
                                                body={(row) => {
                                                    const isSelectable = canSelect(row);
                                                    const isSelected = templateDialogSelected?._id === row._id;
                                                    return (
                                                        <Checkbox
                                                            onChange={() => {
                                                                if (isSelectable) {
                                                                    setTemplateDialogSelected(isSelected ? null : row);
                                                                }
                                                            }}
                                                            checked={isSelected}
                                                            disabled={!isSelectable}
                                                            {...(!isSelectable
                                                                ? { tooltip: 'Please map variables first', tooltipOptions: { position: 'top' } }
                                                                : {}
                                                            )}
                                                        />
                                                    );
                                                }} 
                                                style={{ width: '6rem' }} 
                                            />
                                            <Column field="name" header="Name" sortable />
                                            <Column 
                                                header="Content" 
                                                body={(row) => getContent(row)} 
                                                style={{ maxWidth: '300px', wordBreak: 'break-word' }} 
                                            />
                                            {showTemplateId && (
                                                <Column 
                                                    header="Template ID" 
                                                    body={(row) => getTemplateId(row, key)} 
                                                    style={{ width: '120px' }} 
                                                />
                                            )}
                                            <Column 
                                                header="Variables" 
                                                className="variables-column" 
                                                body={(row) => {
                                                    const content = (() => {
                                                        if (!hasVariables(row)) {
                                                            return <Tag value="No Variables" severity="secondary" />;
                                                        }
                                                        if (isMapped(row)) {
                                                            return (
                                                                <Button
                                                                    icon="pi pi-check-circle"
                                                                    label="Mapped"
                                                                    className="p-button-success p-button-sm p-button-text"
                                                                    onClick={() => handleOpenMappingForTemplate(row, key)}
                                                                    tooltip="Edit mappings"
                                                                />
                                                            );
                                                        }
                                                        return (
                                                            <Button
                                                                icon="pi pi-link"
                                                                label="Map"
                                                                className="p-button-warning p-button-sm"
                                                                onClick={() => handleOpenMappingForTemplate(row, key)}
                                                                tooltip="Map variables to fields"
                                                            />
                                                        );
                                                    })();
                                                    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{content}</div>;
                                                }} 
                                                headerStyle={{ textAlign: 'center' }} 
                                                style={{ width: '140px' }} 
                                            />
                                            <Column 
                                                header="View" 
                                                body={(row) => (
                                                    <Button label="View" className="p-button-sm" text onClick={() => showFullContent(row)} />
                                                )} 
                                                style={{ textAlign: 'right', width: '80px' }} 
                                            />
                                        </DataTable>
                                    </TabPanel>
                                );
                            })}
                        </TabView>
                        <div className="dialog-actions">
                            <Button 
                                label="Save" 
                                onClick={() => handleTemplateAssign(templateDialogWorkflowApiPath, templateDialogActiveChannel, templateDialogSelected)} 
                                disabled={!templateDialogSelected} 
                            />
                            <Button 
                                label="Cancel" 
                                className="p-button-text" 
                                onClick={() => setTemplateDialogVisible(false)} 
                            />
                        </div>
                    </Dialog>

                    <Dialog
                        header="Template Preview"
                        visible={previewDialogVisible}
                        style={{ width: '50vw', maxWidth: 600 }}
                        onHide={() => setPreviewDialogVisible(false)}
                        modal={true}
                    >
                        {previewTemplate && (
                            <div>
                                <div style={{ marginBottom: '1rem' }}>
                                    <strong>Name:</strong> {previewTemplate?.name || 'Unnamed Template'}
                                </div>
                                {previewTemplate.showTemplateId && (
                                    <div style={{ marginBottom: '1rem' }}>
                                        <strong>Template ID:</strong> {
                                            previewTemplate.channelKey === 'whatsapp'
                                                ? (previewTemplate?.templateId || 'No template ID')
                                                : (previewTemplate?.template_id || previewTemplate?.templateId || 'No template ID')
                                        }
                                    </div>
                                )}
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <strong>Content:</strong>
                                    <div style={{
                                        background: '#f9fafb',
                                        padding: '1rem',
                                        borderRadius: '6px',
                                        marginTop: '0.5rem',
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'monospace',
                                        fontSize: '0.9em',
                                        lineHeight: '1.4',
                                        border: '1px solid #e5e7eb'
                                    }}>
                                        {(() => {
                                            const channelKey = previewTemplate.channelKey;
                                            if (channelKey === 'sms') {
                                                return previewTemplate?.content || 'No content available';
                                            } else if (channelKey === 'whatsapp') {
                                                return previewTemplate?.body || 'No content available';
                                            } else if (channelKey === 'email') {
                                                const subject = previewTemplate?.subject || '';
                                                const body = previewTemplate?.body || '';
                                                return subject && body ? `Subject: ${subject}\n\n${body}` : (subject || body || 'No content available');
                                            } else if (channelKey === 'alert') {
                                                return previewTemplate?.description || 'No content available';
                                            }
                                            return 'No content available';
                                        })()}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <Button
                                        label="Close"
                                        onClick={() => setPreviewDialogVisible(false)}
                                        className="p-button-primary"
                                    />
                                </div>
                            </div>
                        )}
                    </Dialog>

                    <VariableMappingDialog
                        visible={variableMappingDialogVisible}
                        onHide={() => setVariableMappingDialogVisible(false)}
                        template={variableMappingData.template}
                        variables={variableMappingData.variables}
                        availableFields={variableMappingData.availableFields}
                        existingMappings={variableMappingData.existingMappings}
                        onSave={handleSaveVariableMappings}
                    />

                    <Dialog
                        header="Add Channel"
                        visible={addChannelDialogVisible}
                        style={{ width: '400px' }}
                        onHide={() => setAddChannelDialogVisible(false)}
                        modal
                    >
                        <div className="add-channel-dialog-content">
                            {addChannelWorkflowApiPath && workflows[addChannelWorkflowApiPath]?.channels
                                ?.filter(ch => !ch.isEnabled)
                                .map(channel => {
                                    const meta = CHANNEL_METADATA[channel.channelKey];
                                    return (
                                        <div 
                                            key={channel.channelKey} 
                                            className="add-channel-option"
                                            onClick={() => {
                                                updateChannel(addChannelWorkflowApiPath, channel.channelKey, { isEnabled: true });
                                                setAddChannelDialogVisible(false);
                                            }}
                                        >
                                            <i className={meta?.icon} style={{ fontSize: '1.5rem', color: '#076EFF' }} />
                                            <div>
                                                <h4>{meta?.title}</h4>
                                                <p>{meta?.caption}</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            {addChannelWorkflowApiPath && workflows[addChannelWorkflowApiPath]?.channels
                                ?.filter(ch => !ch.isEnabled).length === 0 && (
                                <p className="no-channels-message">All channels are already enabled</p>
                            )}
                        </div>
                    </Dialog>
                </>
            )}

            {!loading && Object.keys(workflows).length === 0 && (
                <div className="workflow-empty-state">
                    <i className="pi pi-sync mb-3" />
                    <h4>Unable to load workflows</h4>
                    <p>Something went wrong while fetching the workflows. Please retry.</p>
                    <Button label="Retry" icon="pi pi-refresh" onClick={fetchWorkflow} />
                </div>
            )}
        </div>
    );
};

export default FeeDecisionNotificationWorkflow;
